import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import logo from "../Assets/imgs/main-logo.png";
import { getCookie } from "../utils/Common";
import { hasAnyPermission, hasPermission } from "../utils/permissions";
import "../styles/aces.css";

interface Item {
  to: string;
  label: string;
  icon: string;
  show?: boolean;
}

const COLLAPSED_KEY = "sidebarCollapsedSections";

const readCollapsed = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(COLLAPSED_KEY) || "[]");
  } catch {
    return [];
  }
};

const SideBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<string[]>(readCollapsed);
  const location = useLocation();
  const { t } = useTranslation();

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);
  const cookie: any = getCookie("data");
  const role = cookie.role || null;

  useEffect(() => {
    const toggleHandler = () => setIsOpen((prev) => !prev);
    window.addEventListener("toggleSidebar", toggleHandler);
    return () => {
      window.removeEventListener("toggleSidebar", toggleHandler);
    };
  }, []);

  const handleMenuClick = () => {
    window.dispatchEvent(new Event("toggleSidebar"));
  };

  const toggleSection = (key: string) => {
    const next = collapsed.includes(key) ? collapsed.filter((k) => k !== key) : [...collapsed, key];
    setCollapsed(next);
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next));
  };

  const sections: { key: string; items: Item[] }[] = [
    {
      key: "operations",
      items: [
        { to: "/checklist", label: "aces.nav.checklists", icon: "ri-checkbox-multiple-line", show: role !== "SUPER_ADMIN" },
        { to: "/submittedChecklist", label: "aces.nav.reports", icon: "ri-file-chart-line" },
        {
          to: "/capa", label: "aces.nav.capa", icon: "ri-tools-line",
          show: hasAnyPermission("CAPA_MANAGE", "AI_REVIEW", "CAPA_CLOSE", "CAPA_VERIFY_EFFECTIVENESS", "CAPA_VALIDATE_ROOT_CAUSE"),
        },
      ],
    },
    {
      key: "ai",
      items: [
        { to: "/ai/analyses", label: "aces.nav.analyses", icon: "ri-sparkling-2-line", show: hasAnyPermission("AI_ANALYSE", "AI_REVIEW") },
        { to: "/ai/findings", label: "aces.nav.findings", icon: "ri-search-eye-line", show: hasAnyPermission("AI_ANALYSE", "AI_REVIEW", "CAPA_MANAGE") },
      ],
    },
    {
      key: "communication",
      items: [
        { to: "/chat", label: "aces.nav.teamChat", icon: "ri-chat-3-line" },
        { to: "/notification", label: "aces.nav.notifications", icon: "ri-notification-line" },
      ],
    },
    {
      key: "administration",
      items: [
        { to: "/templatelist", label: "aces.nav.templates", icon: "ri-file-copy-2-line", show: role !== "SUPER_ADMIN" },
        { to: "/usermanagment", label: "aces.nav.users", icon: "ri-group-line" },
        { to: "/settings/sites", label: "aces.nav.sites", icon: "ri-map-pin-line", show: role === "ADMIN" },
        { to: "/audit", label: "aces.nav.audit", icon: "ri-file-list-3-line", show: hasPermission("AUDIT_VIEW") },
        { to: "/admin/ai-settings", label: "aces.nav.aiSettings", icon: "ri-settings-4-line", show: role === "SUPER_ADMIN" },
        { to: "/admin/standards", label: "aces.nav.catalogue", icon: "ri-book-2-line", show: role === "SUPER_ADMIN" },
      ],
    },
    {
      key: "account",
      items: [
        { to: "/profile", label: "aces.nav.profile", icon: "ri-user-line" },
        { to: "/billing", label: "aces.nav.billing", icon: "ri-bank-card-line", show: role !== "USER_MANAGER" },
        { to: "/paymentinquiry", label: "aces.nav.payments", icon: "ri-question-answer-line", show: role !== "USER_MANAGER" },
      ],
    },
  ];

  const link = (item: Item) => (
    <li key={item.to}>
      <Link to={item.to} className={`flex-gap ${isActive(item.to) ? "active" : ""}`} aria-current={isActive(item.to) ? "page" : undefined}>
        <i className={item.icon} aria-hidden="true"></i>
        {t(item.label)}
      </Link>
    </li>
  );

  return (
    <div className={`__sideBar ${isOpen ? "active" : ""}`}>
      <div className="logo">
        <img src={logo} alt="QualiCheck" />
        <button type="button" className="backArrow aces-icon-btn" onClick={handleMenuClick} aria-label={t("aces.a11y.toggleMenu")}>
          <i className="ri-arrow-left-line" aria-hidden="true"></i>
        </button>
      </div>
      <nav aria-label={t("aces.a11y.mainNav")}>
        <ul className="__menuList">
          {link({ to: "/home", label: "aces.nav.home", icon: "ri-home-5-line" })}
          {sections.map(({ key, items }) => {
            const visible = items.filter((i) => i.show !== false);
            if (visible.length === 0) return null;
            // The section holding the current page always stays open.
            const open = !collapsed.includes(key) || visible.some((i) => isActive(i.to));
            return (
              <li key={key} className="aces-nav-section">
                <button type="button" className="aces-nav-heading" aria-expanded={open} aria-controls={`nav-${key}`} onClick={() => toggleSection(key)}>
                  {t(`aces.nav.${key}`)}
                  <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true"></i>
                </button>
                {open && <ul id={`nav-${key}`} className="__menuList">{visible.map(link)}</ul>}
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default SideBar;
